<template>
  <div class="tab-bar">
    <div class="tab-bar-container">
      <div v-for="(tab, index) in tabs" :key="index" class="tab-item"
        :class="{ 'tab-item--active': activeTab === index }" @click="onTabClick(index)">
        <div class="tab-item__icon">
          <img :src="activeTab === index ? tab.iconActive : tab.iconInactive" :alt="t(tab.labelKey)" />
        </div>
        <span class="tab-item__text">{{ t(tab.labelKey) }}</span>
        <span v-if="tab.showDot && !isEditInfo" class="tab-item__dot"></span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { theme } from '@/styles/theme'
import { useI18n } from 'vue-i18n'
// 导入图片资源
import discoverActive from '@/assets/images/home/home_on.svg'
import discoverInactive from '@/assets/images/home/home.svg'
import messageActive from '@/assets/images/home/message_on.svg'
import messageInactive from '@/assets/images/home/message.svg'
import storeActive from '@/assets/images/home/store_on.svg'
import storeInactive from '@/assets/images/home/store.svg'
import mineActive from '@/assets/images/home/mine.svg'
import mineInactive from '@/assets/images/home/mine_on.svg'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const activeTab = ref(0)
const isEditInfo = computed(() => userStore.getIsEditInfo())
const isNavigating = ref(false)

// Tab 配置
interface TabConfig {
  labelKey: string
  path: string
  iconActive: string
  iconInactive: string
  showDot?: boolean
}

const tabs: TabConfig[] = [
// {
//     labelKey: 'nav.recommend',
//     path: '/recommend',
//     iconActive: mineActive,
//     iconInactive: mineInactive,
//     showDot: true
//   },
  {
    labelKey: 'nav.discover',
    path: '/',
    iconActive: discoverActive,
    iconInactive: discoverInactive
  },
  {
    labelKey: 'nav.message',
    path: '/message',
    iconActive: messageActive,
    iconInactive: messageInactive
  },
  {
    labelKey: 'nav.store',
    path: '/store',
    iconActive: storeActive,
    iconInactive: storeInactive
  },
  {
    labelKey: 'nav.mine',
    path: '/mine',
    iconActive: mineActive,
    iconInactive: mineInactive,
    showDot: true
  },

]

// 根据当前路由设置活动标签
const updateActiveTab = () => {
  const currentPath = route.path
  const tabIndex = tabs.findIndex(tab => tab.path === currentPath)

  if (tabIndex !== -1) {
    activeTab.value = tabIndex
  }
}

// 处理标签点击
const onTabClick = async (index: number) => {
  const targetPath = tabs[index].path

  // 防止重复点击
  // if (isNavigating.value) {
  //   return
  // }

  // 如果已经在当前页面，不处理
  if (route.path === targetPath) {
    return
  }

  // isNavigating.value = true

  try {
    // 先更新视觉状态
    activeTab.value = index

    // 跳转路由
    await router.push(targetPath)


    if (userStore.getStoredToken()) {
       userStore.getUserInfo()
    }

  } catch (error: any) {
    console.error('导航失败:', error)

    // 如果不是重复导航错误，恢复之前的tab状态
    if (error?.name !== 'NavigationDuplicated') {
      updateActiveTab()
    }
  } finally {
    // 延迟重置，避免误触
    setTimeout(() => {
      // isNavigating.value = false
    }, 300)
  }
}

// 页面预加载
onMounted(() => {
  setTimeout(() => {
    import('@/views/message/index.vue')
    import('@/views/store/index.vue')
    import('@/views/mine/index.vue')
  }, 500)
})

// 监听路由变化
watch(route, updateActiveTab, { immediate: true })
</script>

<style lang="scss" scoped>
.tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99;
  background: v-bind('theme.colors.background');
  border: none;
  box-shadow: none;
  padding-bottom: env(safe-area-inset-bottom);
}

.tab-bar-container {
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 50px;
  background: v-bind('theme.colors.background');
}

.tab-item {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  // 移除所有过渡动画
  transition: none;

  // 点击效果
  &:active {
    opacity: 0.7;
  }

  &__icon {
    width: 24px;
    height: 24px;
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  &__text {
    font-size: 10px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
    transition: none;
  }

  &__dot {
    position: absolute;
    top: 5px;
    right: calc(50% - 18px);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ff4d8d;
    z-index: 1;
  }

  // 激活状态
  &--active {
    .tab-item__text {
      color: #ffffff;
    }
  }
}
</style>
